/**
 * V2 Segmented — the `.seg` control from design_reference/styles/app.css,
 * built on Radix Tabs for keyboard/ARIA support. Used for view toggles
 * (List/Tree, Form/Generated), theme dark/light, depth selectors, etc.
 *
 * Generic over the value union so callers get type-safe option keys.
 */

import type { ReactNode } from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '../../../lib/utils';

/** A single segmented option. */
export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  /** Optional leading icon node. */
  icon?: ReactNode;
}

/** Props for the V2 Segmented control. */
export interface SegmentedProps<T extends string> {
  value: T;
  onValueChange: (_value: T) => void;
  options: SegmentedOption<T>[];
  /** Stretch each option to fill available width. */
  fullWidth?: boolean;
  className?: string;
}

/** V2 segmented toggle. */
export function Segmented<T extends string>({
  value,
  onValueChange,
  options,
  fullWidth,
  className,
}: SegmentedProps<T>) {
  return (
    <TabsPrimitive.Root
      value={value}
      onValueChange={(v) => onValueChange(v as T)}
      activationMode="manual"
    >
      <TabsPrimitive.List
        className={cn(
          'inline-flex p-[2px] gap-[2px] rounded-[var(--r-md)]',
          'bg-[var(--bg-inset)] border border-[var(--bd-subtle)]',
          fullWidth && 'flex w-full',
          className,
        )}
      >
        {options.map((opt) => (
          <TabsPrimitive.Trigger
            key={opt.value}
            value={opt.value}
            className={cn(
              'inline-flex items-center justify-center gap-[6px] h-[26px] px-[11px] rounded-[var(--r-sm)]',
              'text-[12px] font-[520] text-[var(--tx-lo)] outline-none border-0 bg-transparent',
              'hover:text-[var(--tx-hi)]',
              'focus-visible:ring-2 focus-visible:ring-[var(--accent-line)]',
              'data-[state=active]:bg-[var(--bg-3)] data-[state=active]:text-[var(--tx-hi)]',
              'data-[state=active]:shadow-[inset_0_1px_0_var(--hi-top),var(--sh-sm)]',
              '[&_svg]:w-[14px] [&_svg]:h-[14px]',
              fullWidth && 'flex-1',
            )}
          >
            {opt.icon}
            {opt.label}
          </TabsPrimitive.Trigger>
        ))}
      </TabsPrimitive.List>
    </TabsPrimitive.Root>
  );
}
